import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  Grid,
  Header,
  Icon,
  Modal,
  Input,
  Checkbox,
} from 'semantic-ui-react';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import { useTranslation, withTranslation } from 'react-i18next';
import { namespaces, TRANSLATION_FILES } from './../constants/translation';
import LoanAmount from './LoanAmount';
import CurrencySymbol from './CurrencySymbol';

const DragHandle = SortableHandle(() => <Icon size="large" disabled name="content" />);

const SortableItem = SortableElement(({
  projectTiers,
  data,
  idx,
  onChange,
  onDeleteItem,
  isExistingData,
  isModal,
}) => (
  <Grid className="item">
    <Grid.Row verticalAlign="middle">
      <Grid.Column mobile={3} tablet={2} computer={1}>
        <DragHandle />
      </Grid.Column>
      <Grid.Column mobile={10} tablet={12} computer={isModal ? 13 : 14}>
        { isExistingData && data.projectPricing ?
          <div>
            <p>
              <b>{data.projectPricing.name}</b><br />
              {data.projectPricing.description}
            </p>
            <Grid>
              <Grid.Row verticalAlign="middle">
                <Grid.Column mobile={16} tablet={6} computer={isModal ? 5 : 4}>
                  <Checkbox
                    label="Custom Price"
                    checked={data.isCustomPrice}
                    onChange={(e, d) => onChange(e, d, idx, 'isCustomPrice')}
                  />
                </Grid.Column>
                <Grid.Column mobile={16} tablet={10} computer={isModal ? 11 : 12}>
                  <Input
                    fluid
                    size="small"
                    onChange={(e, d) => onChange(e, d, idx, 'totalPrice')}
                    value={data.totalPrice}
                    type="number"
                    step=".01"
                    disabled={!data.isCustomPrice}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </div>
          :
          <Form.Group widths="equal">
            <Form.Select
              fluid
              onChange={(e, d) => onChange(e, d, idx, 'projectPricingID')}
              options={projectTiers}
              placeholder="Select Project Discount Package"
              search
              value={data.projectPricingID}
            />
          </Form.Group>
        }
      </Grid.Column>
      <Grid.Column mobile={3} tablet={2} computer={isModal ? 2 : 1} className="colRight">
        <Button
          onClick={e => onDeleteItem(e, idx)}
          negative
          icon="trash"
          compact
        />
      </Grid.Column>
    </Grid.Row>
  </Grid>
));

const SortableList = SortableContainer(({
  addButtonIsDisabled,
  projectTiers,
  items,
  onAdd,
  onChange,
  onDeleteItem,
  isModal,
}) => (
  <div className="contain">
    {items.map((data, index) => (
      <SortableItem
        data={data}
        idx={index}
        index={index}
        key={`item-${index}`}
        onChange={onChange}
        onDeleteItem={onDeleteItem}
        projectTiers={projectTiers}
        isExistingData={!!data.projectPricing}
        isModal={isModal}
      />
    ))}
    <Grid className="foot">
      <Grid.Row verticalAlign="middle">
        <Grid.Column textAlign="center">
          <Button
            fluid
            onClick={onAdd}
            disabled={addButtonIsDisabled()}
          >
            Add Discount Package
          </Button>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </div>
));

export const ProjectUnitPricingTierView = ({
  tiers,
  spaValue,
  projectCurrencies = [],
  sellingPriceCustomName,
}) => {
  const { t } = useTranslation(TRANSLATION_FILES.screens);
  const items = [{
    totalPrice: spaValue,
    projectPricing: {
      name: sellingPriceCustomName || t('Selling Price'),
      description: t('Unit Base Selling Price'),
    },
    isSPAValue: true,
  }];
  tiers.sort((a, b) => a.position - b.position).forEach((d) => {
    const newItem = {
      totalPrice: d.totalPrice,
      isSPAValue: false,
    };
    if (d.projectPricing) {
      newItem.projectPricing = {
        name: d.projectPricing.name,
        description: d.projectPricing.description,
      };
    }
    items.push(newItem);
  });
  return (
    <Grid className="projectPricingTierView">
      {items.map((data, idx) => (
        <Grid.Row key={idx} verticalAlign="middle">
          <Grid.Column width={8} textAlign="right">
            <h3 className={data.isSPAValue ? 'spaValue' : ''}>
              <LoanAmount
                projectCurrencies={projectCurrencies}
                format="0,000,000.00"
                amount={data.totalPrice && data.totalPrice ? data.totalPrice : ''}
                prefix={<CurrencySymbol />}
                precise={false}
              />
            </h3>
          </Grid.Column>
          <Grid.Column width={8}>
            <Header as="h4" className={data.isSPAValue ? 'spaValue' : ''}>
              {data.projectPricing && data.projectPricing.name ? data.projectPricing.name : ''}
              <Header.Subheader>
                {data.projectPricing && data.projectPricing.description ? data.projectPricing.description : ''}
              </Header.Subheader>
            </Header>
          </Grid.Column>
        </Grid.Row>
      ))}
    </Grid>
  );
};

ProjectUnitPricingTierView.propTypes = {
  projectCurrencies: PropTypes.oneOfType([PropTypes.array]),
  tiers: PropTypes.oneOfType([PropTypes.array]).isRequired,
  spaValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  sellingPriceCustomName: PropTypes.string,
};


class ProjectUnitPricingTierEdit extends Component {
  static propTypes = {
    data: PropTypes.oneOfType([PropTypes.array]).isRequired,
    projectTiers: PropTypes.oneOfType([PropTypes.array]).isRequired,
    onDelete: PropTypes.func,
    onChangeData: PropTypes.func.isRequired,
    isModal: PropTypes.bool,
  }
  static defaultValues = {
    id: '',
    projectPricingID: '',
    position: 1,
    totalPrice: 0,
    tierName: '',
  };
  static getPropsToState = (props) => {
    const data = props.data || [];
    const projectTiers = props.projectTiers || [];
    return {
      data,
      projectTiers,
    };
  }
  constructor(props) {
    super(props);
    const state = ProjectUnitPricingTierEdit.getPropsToState(props);
    this.state = {
      ...state,
      deleteModalIsOpen: false,
      deleteItemName: '',
      deleteItemIndex: null,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = ProjectUnitPricingTierEdit.getPropsToState(nextProps);
    if (JSON.stringify(this.state.data) !== JSON.stringify(state.data)) {
      this.setState({ ...state });
    }
  }
  onSortEnd = ({ oldIndex, newIndex }) => {
    this.sortDataByIndex(arrayMove(this.state.data, oldIndex, newIndex));
  };
  getData = (data) => {
    const newData = data;
    newData.forEach((d, idx) => {
      if (d.projectPricing && d.projectPricing.name) {
        newData[idx].tierName = d.projectPricing.name;
      }
    });
    return this.getSortedData(newData);
  }
  getprojectTiers = () => {
    const data = this.state.data;
    const projectTiers = this.state.projectTiers.filter(p => !data.find(d => d.id !== '' && d.projectPricingID === p.id)).sort((a, b) => a.position - b.position);
    const options = [];
    projectTiers.forEach((p) => {
      const newItem = {
        text: p.name,
        value: p.id,
        description: p.description,
      };
      const selectedItem = data.find(d => d.projectPricingID === p.id);
      if (selectedItem) newItem.disabled = true;
      options.push(newItem);
    });
    return options;
  }
  getSortedData = data => data.sort((a, b) => a.position - b.position);
  sortDataByIndex = (newData) => {
    const data = newData;
    data.forEach((d, index) => {
      data[index].position = index + 1;
    });
    this.setState({ data }, () => this.props.onChangeData(this.state.data));
  }
  handleOnChange = (e, data, index, type) => {
    const newData = this.state.data;
    if (type === 'projectPricingID') {
      const item = this.state.projectTiers.find(d => d.id === data.value);
      newData[index].projectPricingID = data.value;
      newData[index].tierName = item.name;
      if (this.props.isModal) {
        newData[index].projectPricing = {
          name: item.name,
        };
      }
    }
    if (type === 'totalPrice') {
      newData[index].totalPrice = data.value;
    }
    if (type === 'isCustomPrice') {
      newData[index].isCustomPrice = !newData[index].isCustomPrice;
    }
    this.setState({ data: newData }, () => this.props.onChangeData(this.state.data));
  }
  handleOnAdd = (e) => {
    e.preventDefault();
    const data = this.state.data;
    const lastID = data.length;
    const newItem = Object.assign({}, ProjectUnitPricingTierEdit.defaultValues);
    if (lastID > 0) {
      newItem.position = data[lastID - 1].position;
    }
    data.push(newItem);
    this.sortDataByIndex(data);
  }
  handleOnDelete = (e, index) => {
    e.preventDefault();
    const data = this.state.data;
    this.setState({
      deleteModalIsOpen: true,
      deleteItemName: data[index].tierName,
      deleteItemIndex: index,
    });
  }
  handleDeleteModalConfirm = () => {
    const data = this.state.data;
    let deleteID = '';
    if (data[this.state.deleteItemIndex] && data[this.state.deleteItemIndex].id) {
      deleteID = data[this.state.deleteItemIndex].id;
    }
    if (deleteID !== '' && this.props.onDelete) {
      this.props.onDelete(deleteID);
    } else {
      data.splice(this.state.deleteItemIndex, 1);
      this.sortDataByIndex(data);
    }
    this.setState({
      deleteModalIsOpen: false,
      deleteItemName: '',
      deleteItemIndex: null,
    });
  }
  handleDeleteModalCancel = () => {
    this.setState({
      deleteModalIsOpen: false,
      deleteItemName: '',
      deleteItemIndex: null,
    });
  }
  checkAddButtonDisabled = () => {
    const tiers = this.getprojectTiers().filter(d => !d.disabled).length;
    const blankCount = this.state.data.filter(d => d.projectPricingID === '').length;
    if (blankCount > 0 || tiers < 1) {
      return true;
    }
    return false;
  }
  render() {
    const {
      data,
      deleteItemName,
      deleteModalIsOpen,
    } = this.state;
    const { t } = this.props;
    return (
      <Grid className="projectUnitPricingTier">
        <Grid.Row>
          <Grid.Column width={16}>
            <Modal open={deleteModalIsOpen} basic size="small">
              <Header icon="trash" content={t('Delete Confirmation')} />
              <Modal.Content>
                <h4>{t('Are you sure you want to delete this discount package')}?{deleteItemName ? ` (${deleteItemName})` : ''}</h4>
              </Modal.Content>
              <Modal.Actions>
                <Button basic color="red" inverted onClick={this.handleDeleteModalCancel}>
                  <Icon name="remove" /> {t('No', { ns: TRANSLATION_FILES.constants })}
                </Button>
                <Button color="green" inverted onClick={this.handleDeleteModalConfirm}>
                  <Icon name="checkmark" /> {t('Delete', { ns: TRANSLATION_FILES.translation })}
                </Button>
              </Modal.Actions>
            </Modal>
            <SortableList
              addButtonIsDisabled={this.checkAddButtonDisabled}
              helperClass="projectUnitPricingTier isSorting"
              items={this.getData(data)}
              lockAxis="y"
              lockToContainerEdges
              onAdd={this.handleOnAdd}
              onChange={this.handleOnChange}
              onDeleteItem={this.handleOnDelete}
              onSortEnd={this.onSortEnd}
              projectTiers={this.getprojectTiers()}
              isModal={this.props.isModal}
              useDragHandle
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation(namespaces)(ProjectUnitPricingTierEdit);



// WEBPACK FOOTER //
// ./src/components/ProjectUnitPricingTier.jsx